import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import { AlertCircle } from 'lucide-react';

const VideoPlayer = forwardRef(({ videoId, onTimeUpdate, onStateChange }, ref) => {
    const playerRef = useRef(null);
    const containerRef = useRef(null);
    const [error, setError] = useState(null);
    const onTimeUpdateRef = useRef(onTimeUpdate);
    const onStateChangeRef = useRef(onStateChange);
    onTimeUpdateRef.current = onTimeUpdate;
    onStateChangeRef.current = onStateChange;

    useImperativeHandle(ref, () => ({
        seekTo: (seconds) => {
            if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
                playerRef.current.seekTo(seconds, true);
            }
        },
        pauseVideo: () => {
            if (playerRef.current && typeof playerRef.current.pauseVideo === 'function') {
                playerRef.current.pauseVideo();
            }
        },
        playVideo: () => {
            if (playerRef.current && typeof playerRef.current.playVideo === 'function') {
                playerRef.current.playVideo();
            }
        }
    }));

    useEffect(() => {
        let interval;
        let isMounted = true;

        const startPolling = () => {
            clearInterval(interval);
            interval = setInterval(() => {
                if (playerRef.current && typeof playerRef.current.getCurrentTime === 'function') {
                    const time = playerRef.current.getCurrentTime();
                    if (onTimeUpdateRef.current) onTimeUpdateRef.current(time);
                }
            }, 500);
        };

        const stopPolling = () => {
            clearInterval(interval);
        };

        const loadAPI = () => {
            if (!window.YT) {
                const tag = document.createElement('script');
                tag.src = 'https://www.youtube.com/iframe_api';
                const firstScriptTag = document.getElementsByTagName('script')[0];
                firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
            }
        };

        const initPlayer = () => {
            if (!isMounted) return;
            if (!videoId) return;

            const container = containerRef.current;
            if (!container) return;

            container.innerHTML = '<div id="youtube-player-placeholder"></div>';

            playerRef.current = new window.YT.Player('youtube-player-placeholder', {
                videoId: videoId,
                height: '100%',
                width: '100%',
                playerVars: {
                    autoplay: 0,
                    controls: 1,
                    rel: 0,
                    showinfo: 0,
                    modestbranding: 1,
                    enablejsapi: 1,
                    origin: window.location.origin,
                    host: 'https://www.youtube.com'
                },
                events: {
                    onReady: () => {},
                    onStateChange: (event) => {
                        if (!isMounted) return;
                        const playing = event.data === window.YT.PlayerState.PLAYING;
                        if (playing) {
                            startPolling();
                        } else {
                            stopPolling();
                            // Emit one final time update when pausing so UI stays accurate
                            if (playerRef.current && typeof playerRef.current.getCurrentTime === 'function') {
                                if (onTimeUpdateRef.current) onTimeUpdateRef.current(playerRef.current.getCurrentTime());
                            }
                        }
                        if (onStateChangeRef.current) onStateChangeRef.current(playing);
                    },
                    onError: (event) => {
                        console.error("YouTube Player Error:", event.data);
                        if (isMounted) {
                            let msg = "Error loading video.";
                            if (event.data === 100 || event.data === 101 || event.data === 150) {
                                msg = "Video unavailable or embed disabled.";
                            } else if (event.data === 2) {
                                msg = "Invalid Video ID.";
                            }
                            setError(msg);
                        }
                    }
                }
            });
        };

        loadAPI();

        if (window.YT && window.YT.Player) {
            initPlayer();
        } else {
            const existingCallback = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                if (existingCallback) existingCallback();
                initPlayer();
            };
        }

        return () => {
            isMounted = false;
            stopPolling();
            if (playerRef.current && typeof playerRef.current.destroy === 'function') {
                try {
                    playerRef.current.destroy();
                } catch (e) { /* ignore */ }
            }
        };
    }, [videoId]);

    return (
        <div className="aspect-video bg-black rounded-xl overflow-hidden shadow-2xl border border-white/10 relative group">
            {error ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-2">
                    <AlertCircle size={48} className="text-red-500" />
                    <p>{error}</p>
                </div>
            ) : (
                <div ref={containerRef} className="w-full h-full">
                    <div id="youtube-player-placeholder"></div>
                </div>
            )}
        </div>
    );
});

export default VideoPlayer;
